import Tracker from "@openreplay/tracker";
import { useAuthenticationStore, type TokenData } from "@/stores/authentication";
import { watch, type App } from "vue";
import createOpenReplayPiniaPlugin from "./openreplayPinia";

type CurrentUser = TokenData;

export const openReplayPlugin = {
  install(app: App) {
    if (!this.enabled) return;

    const orig = app.config.errorHandler;
    app.config.errorHandler = (err: any, ins, info) => {
      orig?.(err, ins, info);
      let name, stack, message;
      if (err instanceof Error) {
        name = `Unhandled Error at App Level: ${err.name}`;
        stack = err.stack;
        message = {
          error_message: err.message,
          route: ins?.$route.fullPath,
          componentName: ins?.$options.name || ins?.$options.__name,
          info
        };
      } else {
        name = `Unhandled Error at App Level: ${err?.message || "unknown error"}`;
        stack = err?.stack || "no stack";
        message = {
          error_message: err.message,
          route: ins?.$route.fullPath,
          componentName: ins?.$options.name || ins?.$options.__name,
          info
        };
      }
      this.captureException(name, message, err, stack);
    };

    // Also capture window-level errors and unhandled promise rejections
    try {
      window.addEventListener("error", (e) => this.tracker.handleError(e as any));
      window.addEventListener("unhandledrejection", (e) => this.tracker.handleError(e as any));
    } catch {
      // no-op
    }

    // Optionally register Pinia plugin; controlled by env flag
    if (this.piniaEnabled) {
      // Register Pinia plugin with proper timing - use setTimeout to ensure all plugins are loaded
      setTimeout(() => {
        try {
          const pinia = (app as any)._context?.provides?.pinia;
          console.log('🔧 OpenReplay: Attempting Pinia registration with delay:', !!pinia);
          console.log('🔧 OpenReplay: App context:', !!(app as any)._context);
          console.log('🔧 OpenReplay: App provides:', !!(app as any)._context?.provides);
          if (pinia) {
            pinia.use(createOpenReplayPiniaPlugin(this.tracker));
            console.log('✅ OpenReplay: Pinia plugin registered successfully');
          } else {
            console.warn('⚠️ OpenReplay: Pinia not found with delay - trying alternative approach');
            // Alternative: Try to find pinia through the app instance
            const appInstance = app as any;
            if (appInstance.config?.globalProperties?.$pinia) {
              appInstance.config.globalProperties.$pinia.use(createOpenReplayPiniaPlugin(this.tracker));
              console.log('✅ OpenReplay: Pinia plugin registered via globalProperties');
            } else {
              console.error('❌ OpenReplay: Could not find Pinia instance anywhere');
            }
          }
        } catch (error) {
          console.error('❌ OpenReplay: Error registering Pinia plugin:', error);
        }
      }, 100); // Small delay to ensure all plugins are registered
    } else {
      console.log('⏸️ OpenReplay: Pinia tracking disabled via VITE_APP_OPENREPLAY_PINIA_ENABLE');
    }

    // Test connectivity to OpenReplay before starting
    console.log('🔍 OpenReplay: Testing connectivity to OpenReplay servers...');
    // Use the correct OpenReplay ingest endpoint
    fetch('https://api.openreplay.com/ingest', {
      method: 'OPTIONS', // Use OPTIONS for basic connectivity test
      headers: {
        'Content-Type': 'application/json',
      }
    }).then(response => {
      console.log('🔍 OpenReplay: Connectivity test response:', response.status, response.statusText);
      if (!response.ok && response.status !== 405) { // 405 Method Not Allowed is expected for OPTIONS
        console.warn('⚠️ OpenReplay: Server responded with error:', response.status);
      } else {
        console.log('✅ OpenReplay: Basic connectivity successful');
      }
    }).catch(error => {
      console.error('❌ OpenReplay: Connectivity test failed:', error);
      console.log('💡 This might be a CORS issue or network problem');
    });

    // Start tracker after plugins are registered
    console.log('🚀 OpenReplay: Starting tracker with project key:', import.meta.env.VITE_APP_OPENREPLAY_KEY);
    console.log('🚀 OpenReplay: Debug mode:', import.meta.env.VITE_ENV_MODE === "development");
    
    try {
      this.tracker.start();
      console.log('🚀 OpenReplay: Tracker started successfully');
      
      // Test connection by sending a custom event
      setTimeout(() => {
        this.tracker.event('tracker_test', { test: true, timestamp: Date.now() });
        console.log('🧪 OpenReplay: Test event sent');
        
        // Add global test function for Pinia tracking
        (window as any).testOpenReplayPinia = () => {
          console.log('🧪 Testing OpenReplay Pinia tracking...');
          try {
            // Import and test authentication store
            import('@/stores/authentication').then(({ useAuthenticationStore }) => {
              const authStore = useAuthenticationStore();
              const oldModal = authStore.modal;
              authStore.modal = !authStore.modal;
              console.log('🧪 Authentication store modal changed from', oldModal, 'to', authStore.modal);
              this.tracker.event('pinia_test', { 
                store: 'authentication', 
                property: 'modal', 
                from: oldModal, 
                to: authStore.modal 
              });
            });
          } catch (error) {
            console.error('❌ Failed to test Pinia tracking:', error);
          }
        };
        console.log('🧪 Global test function available: window.testOpenReplayPinia()');
        
      }, 1000);
      
    } catch (error) {
      console.error('❌ OpenReplay: Failed to start tracker:', error);
    }

    // Identify user and watch for changes
    const authStore = useAuthenticationStore();
    if (!authStore?.account){
      authStore.account='support';
    }
    if (authStore.jwtData?.uid) this.identifyUser(authStore.jwtData, authStore.account);
    watch(
      () => authStore.jwtData?.uid,
      () => {
        if (authStore.jwtData?.uid) this.identifyUser(authStore.jwtData, authStore.account);
      }
    );
  },
  identifyUser(user: CurrentUser, account: string) {
    if (this.currentUser.uid !== user.uid || this.account !== account) {
      if (!("uid" in user)) {
        this.tracker.stop();
        this.tracker.start();
      } else {
        this.account = account;
        this.tracker.setUserID(account + " - " + user.uid);
        this.tracker.setMetadata("school_id", user.school_id + "");
        this.tracker.setMetadata("district_id", user.district_id + "");
        this.tracker.setMetadata("subdomain", account);
        this.tracker.setMetadata("uid", user.uid);
        this.tracker.setMetadata("role", user.authentication_type);
      }
    }
    this.currentUser = user;
  },
  captureException(name: string, message: Record<string, any>, error: any, stack?: string) {
    this.tracker.handleError(error, {
      name,
      message,
      stack
    });
  },
  id: import.meta.env.VITE_APP_OPENREPLAY_KEY,
  enabled: import.meta.env.VITE_APP_OPENREPLAY_ENABLE == "1",
  piniaEnabled: import.meta.env.VITE_APP_OPENREPLAY_PINIA_ENABLE == "1",
  currentUser: {} as CurrentUser,
  account: "",
  tracker: new Tracker({
    projectKey: import.meta.env.VITE_APP_OPENREPLAY_KEY,
    __DISABLE_SECURE_MODE: import.meta.env.VITE_ENV_MODE === "development",
    // Add debugging and connection options
    captureIFrames: true,
    respectDoNotTrack: false, // Disable for testing
    // Add ingest point for debugging
    ingestPoint: "https://api.openreplay.com/ingest",
    // Do not obscure emails or numbers in text nodes
    obscureTextEmails: false,
    obscureTextNumbers: false,
    // Privacy: Only hide passwords; do not hide emails or usernames
    defaultInputMode: 0, // Plain
    obscureInputEmails: false,
    obscureInputNumbers: false,
    obscureInputDates: false,
    network: {
      capturePayload: true,
      failuresOnly: false,
      ignoreHeaders: ["Authorization", "authorization"],
      // Attach session token header to requests for easier correlation in backends/logs
      sessionTokenHeader: "X-OpenReplay-Session",
      captureInIframes: false,
      sanitizer(data) {
        const clone = {
          ...data,
          request: {
            ...data.request,
            headers: Object.fromEntries(
              Object.entries(data.request.headers || {}).filter(
                ([k]) => k.toLowerCase() !== "authorization"
              )
            )
          },
          response: {
            ...data.response
          }
        };
        function redactPasswords(body: any) {
          if (body && typeof body === "object") {
            const copy: any = Array.isArray(body) ? [...body] : { ...body };
            for (const key of Object.keys(copy)) {
              if (key.toLowerCase().includes("password")) {
                copy[key] = "***";
              } else if (copy[key] && typeof copy[key] === "object") {
                copy[key] = redactPasswords(copy[key]);
              }
            }
            return copy;
          }
          return body;
        }
        try {
          if (typeof clone.request.body === "string") {
            const parsed = JSON.parse(clone.request.body);
            clone.request.body = JSON.stringify(redactPasswords(parsed));
          }
        } catch {}
        try {
          if (typeof clone.response.body === "string") {
            const parsed = JSON.parse(clone.response.body);
            clone.response.body = JSON.stringify(redactPasswords(parsed));
          }
        } catch {}
        return clone;
      }
    },
    domSanitizer() {
      // Do not hide any DOM nodes globally
      return 0;
    }
  })
};
